/**
 * src/config/billing.ts — billing-mode flag.
 *
 * When true, EVERY customer gets the one-tap shared test card
 * (AlphaCardModal → POST /StripeTestCard) instead of entering their own:
 * the Friends & Family posture, when there were no real pickups to bill for.
 *
 * ⚠️ NOW FALSE. Real customers go through Stripe Elements — `/StripeSetupIntent`
 * for a client secret, `<PaymentElement>` to collect the card (Stripe-direct,
 * our server never sees card data), then POST /StripePaymentMethod to attach it.
 *
 * Test-vs-live is decided by the ENVIRONMENT, not by this flag: dev and staging
 * carry `sk_test_`/`pk_test_` keys, production carries live ones. So the same
 * real flow runs everywhere and dev simply cannot move real money — which is
 * why there is no longer any reason to route around it.
 *
 * e2e fixtures keep the one-tap path regardless, via isTestFixtureEmail. Specs
 * cannot type into a Stripe iframe reliably, and seeding a card is not what
 * they are testing.
 *
 * Turning this back on would silently attach a shared card to real customers
 * and, in live mode, fail instead — `pm_card_visa` is a test-only token.
 */
import { isTestFixtureEmail } from '../utils/testFixtures';

export const ALPHA_BILLING_ENABLED = false;

/**
 * Should this account get the one-tap shared card instead of Stripe Elements?
 *
 * Lives here, in one place, because the decision is made on more than one
 * screen — onboarding's payment step and the dashboard's no-card shortcut. When
 * it was spelled out inline, the dashboard shortcut simply never consulted the
 * flag, so it would have kept handing out the shared card to real customers
 * after the flip.
 */
export function useSharedTestCard(email: string | null | undefined): boolean {
  return ALPHA_BILLING_ENABLED || isTestFixtureEmail(email);
}
